import {
  AmbienteVersion,
  CODIGOS_VERSIONES,
  CrearVersionPeticion,
  EstadoEtapa,
  EstadoVersion,
  EtapaVersiones,
  IncidenciaVersion,
  IndicadoresEtapa,
  RespuestaVersiones,
  VersionResumen,
} from "./etapas-versiones.model";

/*
 * Etapas y versiones: funciones puras para pintar lo que manda el servidor
 * (plan docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §8). No
 * recalculan reglas: el estado de etapa, la vigente, los conteos y lo que
 * puede el usuario vienen calculados. Lo que sí hacen aquí:
 *
 * - textos y clases de los chips de estado (etapa y versión);
 * - fechas en dd/MM/yyyy con la zona del módulo (Ciudad de México), con los
 *   mismos cuatro formatos que lee FechaEtapaParser;
 * - el nombre sugerido V{n}-dd-MM-yyyy y si un nombre está libre en la
 *   etapa (D14), como OrdenVersiones del servidor;
 * - la fecha «aprox.» que trae el nombre de una versión vieja (D15);
 * - los textos de la pestaña «Etapas y versiones» (A1): la etapa elegida en
 *   ?etapa=, «3 versiones · V3 vigente», «Desde …» de una cerrada y los
 *   renglones de los indicadores;
 * - el id de idempotencia de T1 y la petición de crear versión;
 * - las resoluciones que esperan versión (T3b, Fase 3): «N resoluciones
 *   esperan versión», el renglón de cada una y los avisos de T1 (§1.3, D3);
 * - el rechazo del guardado completo del proyecto (§5.8) para los editores.
 */

/** La zona de todo el módulo, como FechaEtapaParser.MEXICO. */
export const ZONA_MEXICO = "America/Mexico_City";

/** Intentos para encontrar un nombre sugerido libre, como MAXIMO_INTENTOS_NOMBRE del servidor. */
const MAXIMO_INTENTOS_NOMBRE = 1000;

const NOMBRE_CON_FECHA = /^V(\d+)-(\d{2})-(\d{2})-(\d{4})$/i;
/* \d\d es lo mismo que \d{2}; así la expresión se queda en la complejidad que permite Sonar (S5843). */
const ISO_CON_ZONA = /^(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d)(?::(\d\d)(?:\.(\d{1,9}))?)?(Z|[+-]\d\d:?\d\d)$/i;
const ISO_SIN_ZONA = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?$/;
const ISO_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;
const DIA_MES_ANIO = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const ESTATUS_CERRADOS = new Set(["CERRADO", "CIERRE"]);
/** Lo que se pinta donde no hay dato. */
export const SIN_DATO = "—";
/** Prefijo de la clave de una etapa guardada sin _id: su posición en la lista (1, 2, …). */
const PREFIJO_POSICION = "pos-";

/** Un chip de estado: texto, modificador de table-page__status-badge, ícono de Material y el detalle. */
export interface ChipEstado {
  texto: string;
  /** is-active · is-pending · is-inactive (styles.css, table-page__status-badge). */
  clase: string;
  icono: string;
  /** «Día 13 de 31», «Fuera de fecha», «Inicia el …», «Terminó el …»; null si no hay. */
  detalle: string | null;
}

/** Los valores del formulario de T1 tal como se capturaron. */
export interface ValoresNuevaVersion {
  nombre: string | null;
  ambiente: AmbienteVersion | null;
  origen: string | null;
  ruta: string | null;
  detalle: string | null;
}

/** Lo que da T1 al cerrar (ResultadoNuevaVersion), con lo que se lee para el aviso. */
export interface VersionCreada {
  version: { nombre?: string | null } | null;
  versionCerrada?: { nombre?: string | null } | null;
  /** Las resoluciones que entregó la versión (T3b); vacío si no se llevó ninguna. */
  incidenciasEntregadas?: readonly unknown[] | null;
}

/** Lo que se lee de una versión para nombrarla y ordenarla. */
type VersionNombrada = Pick<VersionResumen, "nombre"> & Partial<Pick<VersionResumen, "consecutivo">>;

/* ============================ Chips ============================ */

/** El chip de una etapa a partir del EstadoEtapaBean del servidor. */
export function chipEstadoEtapa(estado: EstadoEtapa | null | undefined): ChipEstado {
  const detalle = estado?.texto?.trim() || null;
  if (!estado) {
    return { texto: "Sin estado", clase: "is-inactive", icono: "help_outline", detalle };
  }
  switch (estado.codigo) {
    case "POR_INICIAR":
      return { texto: "Por iniciar", clase: "is-inactive", icono: "event", detalle };
    case "ACTIVA":
      return estado.fueraDeFecha
        ? { texto: "Activa", clase: "is-pending", icono: "schedule", detalle: detalle ?? "Fuera de fecha" }
        : { texto: "Activa", clase: "is-active", icono: "play_circle", detalle };
    case "TERMINADA":
      return { texto: "Terminada", clase: "is-inactive", icono: "check_circle", detalle };
    default:
      return { texto: "Sin estado", clase: "is-inactive", icono: "help_outline", detalle };
  }
}

/**
 * Los chips de una etapa en A1, como en la maqueta: el estado y, si ya pasó
 * su fecha fin con incidencias abiertas, «Fuera de fecha» aparte (la etapa
 * sigue Activa).
 */
export function chipsEstadoEtapa(estado: EstadoEtapa | null | undefined): ChipEstado[] {
  const chip = chipEstadoEtapa(estado);
  if (estado?.codigo !== "ACTIVA" || !estado.fueraDeFecha) {
    return [chip];
  }
  return [
    { texto: chip.texto, clase: "is-active", icono: "play_circle", detalle: null },
    { texto: "Fuera de fecha", clase: "is-pending", icono: "schedule", detalle: null },
  ];
}

/** El chip de una versión: Vigente (verde, palomita) o Cerrada (gris, candado). */
export function chipEstadoVersion(estado: EstadoVersion | null | undefined): ChipEstado {
  if (estado === "VIGENTE") {
    return { texto: "Vigente", clase: "is-active", icono: "check", detalle: null };
  }
  if (estado === "CERRADA") {
    return { texto: "Cerrada", clase: "is-inactive", icono: "lock", detalle: null };
  }
  return { texto: "—", clase: "is-inactive", icono: "help_outline", detalle: null };
}

/* ============================ Fechas ============================ */

/** El día (yyyy-MM-dd) de un instante en la Ciudad de México. */
export function diaEnMexico(instante: Date): string {
  const partes = new Intl.DateTimeFormat("en-US", {
    timeZone: ZONA_MEXICO,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instante);
  const parte = (tipo: string) => partes.find((p) => p.type === tipo)?.value ?? "";
  return `${parte("year")}-${parte("month")}-${parte("day")}`;
}

/**
 * El día (yyyy-MM-dd) que dice un texto de fecha, con los formatos de
 * FechaEtapaParser y en su orden: instante ISO con zona (el día de la Ciudad
 * de México en que ocurrió: para fechaCreacion y otros momentos reales), ISO
 * sin zona (el día tal cual), yyyy-MM-dd y d/M/yyyy. Una fecha imposible
 * (31/02) o cualquier otra cosa da null. Las fechas de etapa van con
 * diaDeFechaDeEtapa.
 */
export function diaDeTexto(texto: string | null | undefined): string | null {
  return leerDia(texto, diaEnMexico);
}

/**
 * Como diaDeTexto, pero para fechaInicio y fechaFin de una etapa, igual que
 * FechaEtapaParser: un instante ISO con zona es la medianoche local del
 * navegador que lo capturó (el datepicker manda un Date a las 00:00), así que
 * se toma la medianoche de la Ciudad de México más cercana (desde las 12:00
 * de México es el día siguiente). Así una fecha capturada en UTC-5 (Cancún, o
 * antes de que se quitara el horario de verano), en el noroeste o en un
 * navegador en UTC no se corre un día.
 */
export function diaDeFechaDeEtapa(texto: string | null | undefined): string | null {
  return leerDia(texto, medianocheMasCercanaEnMexico);
}

/** dd/MM/yyyy del texto de una fecha (diaDeTexto); null si no se puede leer. */
export function fechaCorta(texto: string | null | undefined): string | null {
  return ddMMyyyy(diaDeTexto(texto));
}

/** «09/07/2026 - 31/07/2026»; «—» en el extremo que no se pueda leer (diaDeFechaDeEtapa). */
export function fechasDeEtapa(etapa: Pick<EtapaVersiones, "fechaInicio" | "fechaFin"> | null | undefined): string {
  const inicio = ddMMyyyy(diaDeFechaDeEtapa(etapa?.fechaInicio));
  const fin = ddMMyyyy(diaDeFechaDeEtapa(etapa?.fechaFin));
  if (!inicio && !fin) {
    return "Sin fechas registradas";
  }
  return `${inicio ?? "—"} - ${fin ?? "—"}`;
}

/** La fecha (yyyy-MM-dd) que trae un nombre V<n>-dd-MM-yyyy; null si no sigue el patrón o no existe (D15). */
export function fechaAproxDesdeNombre(nombre: string | null | undefined): string | null {
  const partes = NOMBRE_CON_FECHA.exec((nombre ?? "").trim());
  return partes ? dia(+partes[4], +partes[3], +partes[2]) : null;
}

/** «Creada 13/08/2026», «Creada 13/08/2026 (aprox.)» (D15) o «Creada: —». */
export function textoCreacion(version: Pick<VersionResumen, "nombre" | "fechaCreacion" | "fechaAprox">): string {
  const fecha = fechaDeVersion(version);
  return fecha === SIN_DATO ? "Creada: —" : `Creada ${fecha}`;
}

/** La columna «Creada» de A1: «13/08/2026», «13/08/2026 (aprox.)» (D15) o «—». */
export function fechaDeVersion(version: Pick<VersionResumen, "nombre" | "fechaCreacion" | "fechaAprox">): string {
  const exacta = fechaCorta(version.fechaCreacion);
  if (exacta) {
    return exacta;
  }
  const aprox = fechaCorta(version.fechaAprox ?? fechaAproxDesdeNombre(version.nombre));
  return aprox ? `${aprox} (aprox.)` : SIN_DATO;
}

/** «QA · Creada 13/08/2026»: el renglón de una versión bajo su nombre. */
export function lineaVersion(version: Pick<VersionResumen, "nombre" | "ambiente" | "fechaCreacion" | "fechaAprox">): string {
  return [version.ambiente?.trim(), textoCreacion(version)].filter(Boolean).join(" · ");
}

/* ============================ Nombres y orden ============================ */

/** Si el nombre está libre en la etapa: único sin distinguir mayúsculas ni espacios en los extremos (D14). */
export function nombreLibre(versiones: readonly VersionNombrada[] | null | undefined, nombre: string | null | undefined): boolean {
  const buscado = (nombre ?? "").trim().toLowerCase();
  if (!buscado) {
    return true;
  }
  return !(versiones ?? []).some((v) => (v?.nombre ?? "").trim().toLowerCase() === buscado);
}

/** max(orden) + 1, con el orden de una versión vieja = su posición + 1 (§5.1). */
export function siguienteConsecutivo(versiones: readonly VersionNombrada[] | null | undefined): number {
  let mayor = 0;
  (versiones ?? []).forEach((v, i) => {
    mayor = Math.max(mayor, v?.consecutivo ?? i + 1);
  });
  return mayor + 1;
}

/**
 * El nombre que propone T1 si el servidor no lo mandó: V{siguiente}-dd-MM-yyyy
 * con el día de hoy en la Ciudad de México; si está tomado, sube el número
 * hasta encontrar uno libre (D14, OrdenVersiones.nombreSugerido).
 */
export function nombreSugerido(versiones: readonly VersionNombrada[] | null | undefined, hoy: Date): string {
  const [anio, mes, d] = diaEnMexico(hoy).split("-");
  const fecha = `${d}-${mes}-${anio}`;
  let numero = siguienteConsecutivo(versiones);
  let nombre = `V${numero}-${fecha}`;
  for (let intento = 0; intento < MAXIMO_INTENTOS_NOMBRE && !nombreLibre(versiones, nombre); intento++) {
    numero++;
    nombre = `V${numero}-${fecha}`;
  }
  return nombre;
}

/**
 * Las versiones de la más nueva a la más vieja (la vigente primero), sin
 * tocar el arreglo. Sirve con las de A1 y con las que trae el proyecto de la
 * lista (orden = consecutivo o posición + 1, §5.1).
 */
export function versionesRecientesPrimero<T extends VersionNombrada>(versiones: readonly T[] | null | undefined): T[] {
  return (versiones ?? [])
    .map((version, indice) => ({ version, orden: version?.consecutivo ?? indice + 1 }))
    .sort((a, b) => b.orden - a.orden)
    .map((fila) => fila.version);
}

/**
 * Las abiertas de la etapa por versión detectada, en el orden de la etapa
 * («1 detectada en V2-16-07-2026»), más las que no son de ninguna versión
 * de la etapa (§4.5).
 */
export function lineasDeAbiertas(etapa: Pick<EtapaVersiones, "versiones" | "indicadores"> | null | undefined): string[] {
  const lineas: string[] = [];
  let enVersiones = 0;
  for (const version of etapa?.versiones ?? []) {
    const abiertas = version?.detectadasAbiertas ?? 0;
    if (abiertas > 0) {
      enVersiones += abiertas;
      lineas.push(`${abiertas} ${abiertas === 1 ? "detectada" : "detectadas"} en ${version.nombre ?? "versión sin nombre"}`);
    }
  }
  const resto = (etapa?.indicadores?.abiertas ?? 0) - enVersiones;
  if (resto > 0) {
    lineas.push(`${resto} sin versión identificada`);
  }
  return lineas;
}

/* ============================ A1: pestaña «Etapas y versiones» ============================ */

/** La clave de una etapa en ?etapa=: su _id o, si se guardó sin él, «pos-N» (su lugar en la lista, desde 1). */
export function claveEtapa(etapa: Pick<EtapaVersiones, "idEtapa"> | null | undefined, indice: number): string {
  return etapa?.idEtapa?.trim() || `${PREFIJO_POSICION}${indice + 1}`;
}

/**
 * La etapa de esa clave; sin clave, o si ya no está, la primera (vienen por
 * fecha de inicio, D12). null si no hay etapas.
 */
export function etapaDeClave<T extends Pick<EtapaVersiones, "idEtapa">>(
  etapas: readonly T[] | null | undefined,
  clave: string | null | undefined,
): T | null {
  const lista = etapas ?? [];
  const buscada = (clave ?? "").trim();
  const indice = buscada ? lista.findIndex((etapa, i) => claveEtapa(etapa, i) === buscada) : -1;
  return lista[Math.max(indice, 0)] ?? null;
}

/** «V3» de un nombre V3-13-08-2026; el nombre tal cual si no sigue el patrón. */
export function nombreCortoVersion(nombre: string | null | undefined): string {
  const limpio = (nombre ?? "").trim();
  const partes = NOMBRE_CON_FECHA.exec(limpio);
  return partes ? `V${partes[1]}` : limpio;
}

/** «3 versiones · V3 vigente», «1 versión · V1 vigente» o «Sin versiones». */
export function resumenVersiones(total: number | null | undefined, nombreVigente: string | null | undefined): string {
  const cuantas = total ?? 0;
  if (cuantas <= 0) {
    return "Sin versiones";
  }
  const vigente = nombreCortoVersion(nombreVigente);
  const texto = plural(cuantas, "versión", "versiones");
  return vigente ? `${texto} · ${vigente} vigente` : texto;
}

/** «1 abierta», «6 abiertas». */
export function textoAbiertasEtapa(abiertas: number | null | undefined): string {
  return plural(abiertas ?? 0, "abierta", "abiertas");
}

/** El title del chip «Fuera de fecha»: «Terminó el 31/07/2026 y tiene 6 incidencias abiertas». */
export function tituloFueraDeFecha(fechaFin: string | null | undefined, abiertasEtapa: number | null | undefined): string {
  const fin = ddMMyyyy(diaDeFechaDeEtapa(fechaFin));
  const abiertas = plural(abiertasEtapa ?? 0, "incidencia abierta", "incidencias abiertas");
  return fin ? `Terminó el ${fin} y tiene ${abiertas}` : `Ya pasó su fecha fin y tiene ${abiertas}`;
}

/** Bajo «Incidencias»: «Detectadas en 3 versiones» o «Sin incidencias registradas». */
export function textoIncidencias(indicadores: IndicadoresEtapa | null | undefined): string {
  if ((indicadores?.incidencias ?? 0) <= 0) {
    return "Sin incidencias registradas";
  }
  return `Detectadas en ${plural(indicadores?.versionesConDetectadas ?? 0, "versión", "versiones")}`;
}

/** Bajo «Abiertas» (D1): «2 por validar · 29 con VoBo». */
export function textoAbiertas(indicadores: IndicadoresEtapa | null | undefined): string {
  return `${indicadores?.porValidar ?? 0} por validar · ${indicadores?.conVobo ?? 0} con VoBo`;
}

/** % de las incidencias de la etapa que ya tienen VoBo (la barra de «Abiertas»), a un decimal; null sin incidencias. */
export function porcentajeConVobo(indicadores: IndicadoresEtapa | null | undefined): number | null {
  const total = indicadores?.incidencias ?? 0;
  if (total <= 0) {
    return null;
  }
  return Math.min(100, Math.round(((indicadores?.conVobo ?? 0) / total) * 1000) / 10);
}

/** «Desde 13/08/2026» o «Desde 13/08/2026 (aprox.)» (D15) de una versión cerrada; null si es la vigente o no se sabe. */
export function textoCerradaDesde(version: Pick<VersionResumen, "estado" | "cerradaDesde" | "cerradaDesdeAprox">): string | null {
  const fecha = version.estado === "CERRADA" ? fechaCorta(version.cerradaDesde) : null;
  if (!fecha) {
    return null;
  }
  return version.cerradaDesdeAprox ? `Desde ${fecha} (aprox.)` : `Desde ${fecha}`;
}

/**
 * El aviso cuando T1 creó la versión: «Se creó la versión V4. V3 pasó a
 * Cerrada.». null si se cerró sin crearla.
 */
export function mensajeVersionCreada(resultado: VersionCreada | null | undefined): string | null {
  if (!resultado?.version) {
    return null;
  }
  const cerrada = resultado.versionCerrada?.nombre ? ` ${resultado.versionCerrada.nombre} pasó a Cerrada.` : "";
  const entregadas = resultado.incidenciasEntregadas?.length ?? 0;
  const entrega = entregadas > 0 ? ` ${textoEntregadasAQA(entregadas)}` : "";
  return `Se creó la versión ${resultado.version.nombre}.${cerrada}${entrega}`;
}

/* ============================ T3b: resoluciones que esperan versión ============================ */

/** La leyenda de A1 con la bandera encendida (§1.3, con «QA» por D3). */
export const LEYENDA_ENTREGA_AUTOMATICA = "Las resoluciones se entregan en la siguiente versión QA de la etapa.";

/** «3 resoluciones esperan versión», «1 resolución espera versión»; null sin pendientes. */
export function textoEsperanVersion(pendientes: number | null | undefined): string | null {
  if (!pendientes || pendientes <= 0) {
    return null;
  }
  return pendientes === 1 ? "1 resolución espera versión" : `${pendientes} resoluciones esperan versión`;
}

/**
 * Una resolución pendiente en T1: «#31 · Alta · Detectada en V3-13-08-2026 ·
 * Resolvió: Luis Ramírez». Prioridad y no severidad (D13); lo que no venga se
 * omite.
 */
export function lineaPendiente(
  incidencia: Pick<IncidenciaVersion, "folio" | "prioridad" | "detectadaEn" | "resolvio">,
): string {
  const prioridad = (incidencia.prioridad ?? "").trim().toLowerCase();
  const detectada = incidencia.detectadaEn?.nombre?.trim();
  const resolvio = incidencia.resolvio?.trim();
  return [
    incidencia.folio === null || incidencia.folio === undefined ? null : `#${incidencia.folio}`,
    prioridad ? prioridad.charAt(0).toUpperCase() + prioridad.slice(1) : null,
    detectada ? `Detectada en ${detectada}` : null,
    resolvio ? `Resolvió: ${resolvio}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * La parte del aviso de T1 que habla de las pendientes (§1.3, D3); null sin
 * pendientes. Una QA se las lleva y pasan a validación de QA; una PRODUCTIVO
 * no: esperan la siguiente versión QA.
 */
export function textoEntregaPendientes(
  pendientes: number | null | undefined,
  nombreNueva: string | null | undefined,
  ambiente: AmbienteVersion | null | undefined,
): string | null {
  if (!pendientes || pendientes <= 0) {
    return null;
  }
  if (ambiente === "PRODUCTIVO") {
    return pendientes === 1
      ? "La resolución pendiente esperará la siguiente versión QA."
      : `Las ${pendientes} pendientes esperarán la siguiente versión QA.`;
  }
  const nombre = (nombreNueva ?? "").trim() || "la versión nueva";
  return pendientes === 1
    ? `La resolución pendiente queda entregada en ${nombre} y pasa a validación de QA.`
    : `Las ${pendientes} resoluciones pendientes quedan entregadas en ${nombre} y pasan a validación de QA.`;
}

/** «Se entregó 1 resolución a QA.», «Se entregaron 3 resoluciones a QA.». */
function textoEntregadasAQA(cuantas: number): string {
  return cuantas === 1 ? "Se entregó 1 resolución a QA." : `Se entregaron ${cuantas} resoluciones a QA.`;
}

/* ============================ Crear versión (T1) ============================ */

/**
 * Por qué no se puede crear (o editar, T1 en edición) la versión cuando
 * puedeCrearVersion es falso: D8 o D4 (editar pide lo mismo que crear).
 */
export function motivoSinPermiso(estatusProyecto: string | null | undefined, edicion = false): string {
  if (ESTATUS_CERRADOS.has((estatusProyecto ?? "").trim().toUpperCase())) {
    return edicion
      ? "El proyecto está cerrado: se puede consultar, pero ya no se editan versiones."
      : "El proyecto está cerrado: se puede consultar, pero ya no se crean versiones.";
  }
  const accion = edicion ? "edita" : "crea";
  return `Solo el líder del proyecto, el líder o el líder QA de la etapa, o quien tenga permiso de proyectos, ${accion} versiones.`;
}

/**
 * Un UUID v4 canónico para la llave de idempotencia de T1. crypto.randomUUID
 * solo existe en contextos seguros (https o localhost); fuera de ellos se
 * arma con getRandomValues, que sí está.
 */
export function nuevoIdVersion(fuente: Pick<Crypto, "getRandomValues"> & { randomUUID?: () => string } = crypto): string {
  if (typeof fuente.randomUUID === "function") {
    return fuente.randomUUID();
  }
  const bytes = fuente.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] % 16) + 64; // versión 4
  bytes[8] = (bytes[8] % 64) + 128; // variante RFC 4122
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * La petición de PROYECTOS_VERSION_CREAR con lo capturado: textos
 * recortados (vacío = null), el origen solo en PRODUCTIVO y la vigente que
 * vio el usuario como idVersionVigenteEsperada (null = sin versiones).
 */
export function peticionCrearVersion(
  id: string,
  idProyecto: string,
  etapa: Pick<EtapaVersiones, "idEtapa" | "vigente">,
  valores: ValoresNuevaVersion,
): CrearVersionPeticion {
  const ambiente: AmbienteVersion = valores.ambiente === "PRODUCTIVO" ? "PRODUCTIVO" : "QA";
  return {
    id,
    idProyecto,
    idEtapa: etapa.idEtapa ?? "",
    nombre: (valores.nombre ?? "").trim(),
    ambiente,
    idVersionOrigen: ambiente === "PRODUCTIVO" ? textoONulo(valores.origen) : null,
    ruta: textoONulo(valores.ruta),
    detalle: textoONulo(valores.detalle),
    idVersionVigenteEsperada: etapa.vigente?.id ?? null,
  };
}

/* ============================ Guardado completo del proyecto ============================ */

/**
 * Si guardarProyecto rechazó el guardado (§5.8 A: VAL-VER-FRONT-VIEJO,
 * VAL-ETAPA-CON-HISTORIAL, VAL-ETAPA-SIN-VERIFICAR), el texto para el usuario;
 * null si no fue un rechazo. Con un rechazo el servidor no guardó nada ni
 * mandó correos ni tocó GitLab. Los demás códigos distintos de 00000 (avisos
 * de GitLab, 00001) llegan también después de guardar, así que cada editor
 * los sigue tratando como antes.
 *
 * Los editores no quitan etapas: si el servidor dice que se quitaba una (con
 * historia, o sin poder comprobarlo), lo más probable es que la copia del
 * proyecto sea anterior a esa etapa, y se dice cómo traerla al día.
 */
export function rechazoDelGuardado(respuesta: Partial<RespuestaVersiones> | null | undefined): string | null {
  const codigo = (respuesta?.pCodRetorno ?? "").trim();
  if (!codigo.startsWith("VAL-")) {
    return null;
  }
  const texto = respuesta?.pMensaje?.trim() || "El servidor rechazó el guardado. No se guardó nada.";
  return codigo === CODIGOS_VERSIONES.ETAPA_CON_HISTORIAL || codigo === CODIGOS_VERSIONES.ETAPA_SIN_VERIFICAR
    ? `${texto} Si no la quitaste, tu copia del proyecto está desactualizada: regresa a la lista y vuelve a abrirlo.`
    : texto;
}

/**
 * La etapa que reemplaza el editor de etapa: primero por id (el _id de Mongo),
 * luego por idEtapa y, solo si ninguno coincide, por nombre. Con OR en una sola
 * búsqueda, dos etapas con el mismo nombre hacían que editar la segunda
 * reemplazara la primera, y el servidor la veía quitada (D11). -1 si no está.
 */
export function indiceDeEtapaEditada<T extends { id?: string | null; idEtapa?: unknown; nombre?: string | null }>(
  etapas: readonly T[],
  editada: T,
): number {
  const porId = editada.id ? etapas.findIndex((e) => e.id === editada.id) : -1;
  if (porId !== -1) {
    return porId;
  }
  const porIdEtapa = editada.idEtapa ? etapas.findIndex((e) => e.idEtapa === editada.idEtapa) : -1;
  return porIdEtapa !== -1 ? porIdEtapa : etapas.findIndex((e) => e.nombre === editada.nombre);
}

/* ============================ Internas ============================ */

/** yyyy-MM-dd si el día existe; null si no (31/02, mes 13). */
function dia(anio: number, mes: number, d: number): string | null {
  const fecha = new Date(Date.UTC(anio, mes - 1, d));
  if (fecha.getUTCFullYear() !== anio || fecha.getUTCMonth() !== mes - 1 || fecha.getUTCDate() !== d) {
    return null;
  }
  return `${String(anio).padStart(4, "0")}-${String(mes).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Los formatos de FechaEtapaParser en su orden; el instante con zona lo resuelve diaDelInstante. */
function leerDia(texto: string | null | undefined, diaDelInstante: (instante: Date) => string): string | null {
  const fecha = (texto ?? "").trim();
  if (!fecha) {
    return null;
  }
  const conZona = ISO_CON_ZONA.exec(fecha);
  if (conZona) {
    const instante = instanteDe(conZona);
    return instante ? diaDelInstante(instante) : null;
  }
  const iso = ISO_SIN_ZONA.exec(fecha) ?? ISO_DIA.exec(fecha);
  if (iso) {
    return dia(+iso[1], +iso[2], +iso[3]);
  }
  const dma = DIA_MES_ANIO.exec(fecha);
  return dma ? dia(+dma[3], +dma[2], +dma[1]) : null;
}

/** dd/MM/yyyy de un yyyy-MM-dd; null si no hay día. */
function ddMMyyyy(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const [anio, mes, d] = iso.split("-");
  return `${d}/${mes}/${anio}`;
}

/** El instante de un ISO con zona ya separado en partes; null si alguna parte no existe. */
function instanteDe(partes: RegExpExecArray): Date | null {
  const [, anio, mes, d, hora, minuto, segundo, fraccion, zona] = partes;
  if (!dia(+anio, +mes, +d) || +hora > 23 || +minuto > 59 || +(segundo ?? 0) > 59) {
    return null;
  }
  const offset = zona.toUpperCase() === "Z" ? "Z" : `${zona.slice(0, 3)}:${zona.slice(-2)}`;
  const milisegundos = (fraccion ?? "").padEnd(3, "0").slice(0, 3);
  const instante = Date.parse(`${anio}-${mes}-${d}T${hora}:${minuto}:${segundo ?? "00"}.${milisegundos}${offset}`);
  return Number.isNaN(instante) ? null : new Date(instante);
}

/** yyyy-MM-dd de la medianoche de la Ciudad de México más cercana al instante. */
function medianocheMasCercanaEnMexico(instante: Date): string {
  const partes = new Intl.DateTimeFormat("en-US", {
    timeZone: ZONA_MEXICO,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instante);
  const parte = (tipo: string) => Number(partes.find((p) => p.type === tipo)?.value);
  const siguiente = parte("hour") >= 12 ? 1 : 0;
  const fecha = new Date(Date.UTC(parte("year"), parte("month") - 1, parte("day") + siguiente));
  return fecha.toISOString().slice(0, 10);
}

/** «1 versión», «3 versiones». */
function plural(cuantas: number, singular: string, varias: string): string {
  return `${cuantas} ${cuantas === 1 ? singular : varias}`;
}

function textoONulo(texto: string | null | undefined): string | null {
  const limpio = (texto ?? "").trim();
  return limpio || null;
}
